import React, { useState } from 'react';
import { dayName, rupees } from '@/components/myPay/i18n';
import { PayCell, PayDetail } from '@/components/myPay/DayPay';

export const TONE = {
  present: 'bg-emerald-100 text-emerald-800', late: 'bg-amber-100 text-amber-800', early: 'bg-orange-100 text-orange-800',
  missed: 'bg-rose-100 text-rose-700', pending: 'bg-sky-100 text-sky-800', rejected: 'bg-red-100 text-red-700',
  absent: 'bg-gray-100 text-gray-600', off: 'bg-slate-100 text-slate-500', auto_closed: 'bg-violet-100 text-violet-700', contact_hr: 'bg-red-100 text-red-700',
};
const COLS = 'sm:grid-cols-[6rem_11rem_1fr_4rem_8rem]';

const Times = ({ d, t }) => (
  <div className="space-y-0.5 font-mono text-[13px]">
    {d.sessions.map((s, i) => (
      <div key={i} data-testid={`mypay-session-${d.date}-${i}`}>
        {s.in || '—'} → {s.out || '—'}
        {(s.in_status === 'pending' || s.out_status === 'pending') && <span className="ml-1 font-sans text-[11px] text-sky-700">({t('waiting')})</span>}
        {(s.in_status === 'rejected' || s.out_status === 'rejected') && <span className="ml-1 font-sans text-[11px] text-red-700">({t('rejected')})</span>}
      </div>
    ))}
  </div>
);

const Row = ({ d, t, lang, open, onToggle, fx }) => {
  const extra = [d.late_min && t('lateBy', { n: d.late_min }), d.early_min && t('earlyBy', { n: d.early_min }), d.off_site && t('offSite')].filter(Boolean);
  const tap = d.pay ? { role: 'button', tabIndex: 0, onClick: onToggle, onKeyDown: (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onToggle()) } : {};
  return (
    <div className={`${d.status === 'off' ? 'bg-slate-50/70' : ''} ${open ? 'bg-[#FFFDF7]' : ''}`} data-testid={`mypay-day-${d.date}`}>
      <div {...tap} className={`grid grid-cols-[4.5rem_1fr_auto] ${COLS} items-center gap-x-3 gap-y-1 px-3 py-2 text-sm ${d.pay ? 'cursor-pointer hover:bg-[#F7F3EA]/70 transition-colors' : ''}`}
        data-testid={`mypay-day-row-${d.date}`}>
        <div><span className="font-semibold tabular-nums">{d.date.slice(8)}</span> <span className="text-xs text-gray-500">{dayName(d.date, lang)}</span></div>
        <div className="min-w-0">
          {d.status !== 'future' && <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${TONE[d.status] || ''}`} data-testid={`mypay-status-${d.date}`}>{t(d.status)}</span>}
          {d.hours > 0 && <span className="sm:hidden ml-2 text-xs tabular-nums text-gray-600" data-testid={`mypay-hours-m-${d.date}`}>{d.hours.toFixed(2)} {t('hours')}</span>}
          {extra.length > 0 && <div className="text-[11px] text-gray-500">{extra.join(' · ')}</div>}
        </div>
        <div className="sm:order-last"><PayCell d={d} t={t} open={open} /></div>
        <div className="col-span-3 sm:col-span-1 pl-[5.25rem] sm:pl-0">{d.sessions.length > 0 && <Times d={d} t={t} />}</div>
        <div className="hidden sm:block text-right tabular-nums text-gray-700" data-testid={`mypay-hours-${d.date}`}>{d.hours ? d.hours.toFixed(2) : ''}</div>
      </div>
      {open && <PayDetail d={d} t={t} fx={fx} />}
    </div>
  );
};

/** Day-by-day: status, every check-in → check-out session, hours and that day's pay (tap a day for the work behind it). */
export const AttendanceTable = ({ days, total, fx, t, lang }) => {
  const [open, setOpen] = useState(null);
  const anyPay = days.some((d) => d.pay);
  return (
    <div className="space-y-1.5">
      {anyPay && <p className="text-xs text-gray-500 print:hidden" data-testid="mypay-tap-hint">{t('tapHint')}</p>}
      <div className="rounded-xl border border-[#E5DFD0] bg-white" data-testid="mypay-attendance">
        <div className={`hidden sm:grid ${COLS} gap-3 border-b bg-[#F7F3EA] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-600`}>
          <span>{t('date')}</span><span>{t('status')}</span><span>{t('inOut')}</span><span className="text-right">{t('hours')}</span><span className="text-right pr-5">{t('dayPay')}</span>
        </div>
        <div className="divide-y divide-[#EFE9DC]">{days.map((d) => (
          <Row key={d.date} d={d} t={t} lang={lang} fx={fx} open={open === d.date} onToggle={() => setOpen(open === d.date ? null : d.date)} />
        ))}</div>
        {anyPay && (
          <div className="flex items-center justify-between rounded-b-xl border-t bg-[#F7F3EA] px-3 py-2 text-sm font-semibold" data-testid="mypay-daypay-footer">
            <span>{t('total')}</span><span className="pr-5 tabular-nums" data-testid="mypay-daypay-footer-total">{rupees(total)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceTable;
